import { Ref, computed, ComputedRef, Reactive } from "vue";
import { useStore } from "vuex";
import useFunctions from "@/use/functions1";
import { $api } from "@/services/api1";
import functions from "@/use/functions";
import { ApplicationStatusIds, CancellationRequest } from "@/types/application";

const { decodeHTMLEncodedStr } = useFunctions();

export interface ApplicationStatusForm {
  applicationStatusId: number;
  newApplicationStatusId?: number | null;
  newStatusName?: string | null;
  statusComment?: string | null;
  otherDenialReason?: string | null;
}

interface ValidationData {
  applicationId: ComputedRef<number>;
  currentApplicationStatusId: ComputedRef<number>;
  info: Reactive<any>;
  version: Ref<number>;
  cancellationReasonFormRef: Ref<any>;
  onApplicationStatusUpdateSuccess: () => void;
}

export function useApplicationStatusForm(data: ValidationData) {
  const rootStore = useStore();
  const oidcStore = computed(() => rootStore.state.oidcStore);
  const userDisplayName = computed(() => {
    return `${decodeHTMLEncodedStr(
      oidcStore.value?.user?.given_name
    )} ${decodeHTMLEncodedStr(oidcStore.value?.user?.family_name)}`;
  });

  const showCancellationReasonForm = computed<boolean>(() => {
    return (
      data.info.newApplicationStatusId === ApplicationStatusIds.CANCELLED &&
      data.currentApplicationStatusId.value !== ApplicationStatusIds.DECLINED
    );
  });

  const cancellationRequest = computed<CancellationRequest>(() => {
    return {
      applicantId: data.cancellationReasonFormRef.value?.selectedApplicantId,
      statusReasonId: data.cancellationReasonFormRef.value?.selectedReasonId,
      cancellationReason:
        data.cancellationReasonFormRef.value?.selectedReason?.description,
    };
  });

  const commentRequest = computed(() => {
    const comment =
      data.info?.newApplicationStatusId === ApplicationStatusIds.CANCELLED
        ? data.cancellationReasonFormRef.value?.comment
        : data.info.statusComment;

    return {
      id: 0,
      applicationId: data.applicationId.value,
      comment: comment,
      userId: oidcStore.value?.user?.sub,
      userDisplayName: userDisplayName.value,
    };
  });

  const cancelApplicationRequest = computed(() => {
    if (data.info?.newApplicationStatusId === ApplicationStatusIds.CANCELLED) {
      return {
        cancellationRequest: cancellationRequest.value,
        commentRequest: commentRequest.value,
      };
    } else {
      return {};
    }
  });

  function closeCancellationReasonForm() {
    // reset the cancellation form and close
    data.cancellationReasonFormRef.value?.toggleForm(false);
    data.cancellationReasonFormRef.value?.resetForm();
  }

  async function cancelApplicationAsync() {
    // Validate Cancellation form
    const response = await $api.applications.cancelApplication(
      data.applicationId.value,
      cancelApplicationRequest.value
    );

    if (response?.errors?.length > 0) {
      functions.openModal({
        title: "Alert",
        description: response.errors[0],
      });

      data.cancellationReasonFormRef.value?.setSavingChanges(false);

      return false;
    } else {
      data.onApplicationStatusUpdateSuccess();
      return true;
    }
  }

  async function updateApplicationStatusAsync() {
    // Validate Cancellation form
    if (data.info?.newApplicationStatusId === ApplicationStatusIds.CANCELLED) {
      const response = await cancelApplicationAsync();
      return response;
    } else {
      const response = await $api.applications.updateApplicationStatus(
        data.applicationId.value,
        data.info?.applicationStatusId ?? data.info?.newApplicationStatusId,
        commentRequest.value
      );

      if (response?.errors?.length > 0) {
        functions.openModal({
          title: "Alert",
          description: response.errors[0],
        });

        return false;
      } else if (!response?.results?.applicationId) {
        functions.openModal({
          title: "Error",
          description: "There was an issue updating the Application Status",
        });

        return false;
      } else {
        data.onApplicationStatusUpdateSuccess();
        // emit("getApplication");
        return true;
      }
    }
  }

  return {
    userDisplayName,
    commentRequest,
    cancellationRequest,
    cancelApplicationRequest,
		showCancellationReasonForm,
    updateApplicationStatusAsync,
    closeCancellationReasonForm,
  };
}
